import Env from "../environments";
import { stringToPrice } from "../utils/currency";
import { getParamUrl } from "../utils/url";
import { MetaProduct } from "./MetaProduct";
import { Note } from "./Note";
import { ProductGroupSite } from "./ProductGroupSite";
import { Site } from "./Site";
import { OrderAction } from "./OrderAction";

export class Product {
  constructor(id, siteId, siteUrl, siteName, siteLogo, sku, meta, currentCurrency, price, quantity, storeDomain, currentUrl, category, screenshot, shippingCurrency, shippingCost, sitePrice, sitePriceCur, type, notes, pid, isLowestPrice, audits) {
    this.id = id;
    this.siteId = siteId;
    this.siteUrl = siteUrl;
    this.siteName = siteName;
    this.siteLogo = siteLogo;
    this.sku = sku;
    /** @type {MetaProduct} */
    this.meta = meta;
    this.currentCurrency = currentCurrency;
    this.price = price;
    this.quantity = quantity;
    this.storeDomain = storeDomain;
    this.currentUrl = currentUrl;
    this.category = category;
    this.screenshot = screenshot;
    this.shippingCurrency = shippingCurrency;
    this.shippingCost = shippingCost;
    this.sitePrice = sitePrice;
    this.sitePriceCur = sitePriceCur;
    this.type = type;
    /** @type {Note} */
    this.notes = notes;
    this.pid = pid;
    this.isLowestPrice = isLowestPrice;
    /** @type {[OrderAction]} */
    this.audits = audits;
  }

  static fromApiObject(input) {
    if (input && typeof (input) === "object") {
      return new Product(
        input[PRODUCT_ATTRIBUTE.ID],
        input[PRODUCT_ATTRIBUTE.SITE_ID],
        getUrlHost(input[PRODUCT_ATTRIBUTE.META], input[PRODUCT_ATTRIBUTE.SITE_ID]),
        getUrlHost(input[PRODUCT_ATTRIBUTE.META], input[PRODUCT_ATTRIBUTE.SITE_ID]),
        input[PRODUCT_ATTRIBUTE.SITE_LOGO],
        input[PRODUCT_ATTRIBUTE.SKU],
        MetaProduct.fromApiObject(input[PRODUCT_ATTRIBUTE.META]),
        input[PRODUCT_ATTRIBUTE.CURRENCY],
        stringToPrice(input[PRODUCT_ATTRIBUTE.PRICE]),
        stringToPrice(input[PRODUCT_ATTRIBUTE.QUANTITY]) || 1,
        input[PRODUCT_ATTRIBUTE.DOMAIN],
        input[PRODUCT_ATTRIBUTE.CURRENT_URL],
        input[PRODUCT_ATTRIBUTE.CATEGORY],
        input[PRODUCT_ATTRIBUTE.SCREENSHOT],
        input[PRODUCT_ATTRIBUTE.SHIPPING_CURRENCY],
        input[PRODUCT_ATTRIBUTE.SHIPPING_COST],
        input[PRODUCT_ATTRIBUTE.SITE_PRICE],
        input[PRODUCT_ATTRIBUTE.SITE_PRICE_CUR],
        input[PRODUCT_ATTRIBUTE.TYPE],
        Note.fromApiArray(input[PRODUCT_ATTRIBUTE.NOTES]),
        input[PRODUCT_ATTRIBUTE.PID],
        input[PRODUCT_ATTRIBUTE.IS_LOWEST_PRICE],
        OrderAction.fromApiArray(input[PRODUCT_ATTRIBUTE.AUDITS]),
      )
    }

    return new Product();
  }

  static fromApiArray(input) {
    if (input && Array.isArray(input)) {
      return input.map(item => Product.fromApiObject(item));
    }

    if (input && typeof (input) === "object") {
      return [Product.fromApiObject(input)];
    }

    return [];
  }

  static genParamsProduct(product, isUpdate = false) {
    var result = {};
    if (product.id) {
      result[PRODUCT_ATTRIBUTE.ID] = product.id;
    }
    if (product.siteId && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.SITE_ID] = product.siteId;
    }
    if (product.sku) {
      result[PRODUCT_ATTRIBUTE.SKU] = product.sku;
    }
    if (product.currentCurrency && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.CURRENCY] = product.currentCurrency;
    }
    if (product.meta && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.META] = MetaProduct.genParamsMeta(product.meta);
    }
    if (product.price && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.PRICE] = product.price;
    }

    result[PRODUCT_ATTRIBUTE.QUANTITY] = product.quantity * 1;

    if (product.category && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.CATEGORY] = product.category;
    }
    if (product.screenshot && !isUpdate) {
      result[PRODUCT_ATTRIBUTE.SCREENSHOT] = product.screenshot;
    }
    return result;
  }

  /**
   * 
   * @param {[Product]} details 
   * @returns {[ProductGroupSite]}
   */
  static groupProductWithSite(details = []) {
    let result = [];
    if (details && details.length > 0) {
      let listSite = [];
      details.forEach(element => {
        if (!listSite.includes(element.siteId)) {
          listSite.push(element.siteId);
          let site = Site.fromApiObject({
            id: element.siteId,
            domain: element.siteUrl,
            name: element.siteName,
            site_meta: {
              url: element.siteUrl,
              logo_url: element.siteLogo
            }
          })
          let products = details.filter(item => item.siteId === element.siteId);
          result.push(new ProductGroupSite(site, products));
        }
      });
    }
    return result;
  }

  canSelected() {
    return this.type === Product.TYPE_PRODUCT.NORMAL.id || this.type === Product.TYPE_PRODUCT.QUOTED.id;
  }

  isNotSelected() {
    return this.type === Product.TYPE_PRODUCT.UNSELECT.id;
  }

  isRFQ() {
    return this.type === Product.TYPE_PRODUCT.RFQ.id;
  }

  getNoteType(type) {
    if (this.notes && this.notes.length === 0) {
      return false;
    }
    let result = false;
    this.notes.forEach(element => {
      if (element.type === type) {
        result = element;
        return true;
      }
    });
    return result;
  }

  static isSelected(type) {
    return !type || type === Product.TYPE_PRODUCT.NORMAL.id || type === Product.TYPE_PRODUCT.QUOTED.id;
  }

  /** @param {[Product]} products */
  static hasProductSelected(products = []) {
    for (let index = 0; index < products.length; index++) {
      const element = products[index];
      if (element.canSelected()) {
        return true;
      }
    }
    return false;
  }

  /** @param {[Product]} products */
  static getParamPriceTags(products = []) {
    let paramsPeriods = getParamUrl("periods");
    let paramsPeriodUnit = getParamUrl("period_unit");
    let periods = (paramsPeriods || Env.DUTY_CAST_PRICE_TAGS_PERIOD || "");
    let params = {
      "period_unit": paramsPeriodUnit || Env.DUTY_CAST_PRICE_TAGS_UNIT,
      "periods": periods ? periods.split(",") : undefined,
      "p": []
    }

    for (let index = 0; index < products.length; index++) {
      const element = products[index];
      if (!element.isRFQ()) {
        params.p.push({
          "id": element.pid || element.id,
          "cp": element.sitePrice,
          "cur": element.sitePriceCur,
        })
      }
    }

    return params;
  }

  static TYPE_PRODUCT = {
    RFQ: {
      id: 'rfq',
      class: 'cart_rfq'
    },
    NORMAL: {
      id: 'normal',
      class: 'cart'
    },
    UNSELECT: {
      id: 'unselect',
      class: 'cart'
    },
    QUOTED: {
      id: 'quoted',
      class: 'cart'
    }
  }

  static TYPE_NOTES = {
    OOS: 'oos',
    MAX_QUANTITY: 'max_quantity',
    LOCATION: 'location',
  }
}

function getUrlHost(meta, defaultValue) {
  if (meta && meta[PRODUCT_ATTRIBUTE.CURRENT_URL]) {
    try {
      var url = new URL(meta[PRODUCT_ATTRIBUTE.CURRENT_URL])
      return (url.host || "").replace('www.', '');
    } catch (_) { }
  }
  return defaultValue;
}

export const PRODUCT_ATTRIBUTE = {
  ID: 'id',
  SITE_ID: "site_id",
  SITE_URL: "site_id",
  SITE_NAME: "site_id",
  SITE_LOGO: "site_logo",
  SKU: "sku",
  META: 'meta',
  CURRENCY: "cur",
  PRICE: "unit_price",
  QUANTITY: "quantity",
  DOMAIN: "domain",
  CURRENT_URL: "current_url",
  SCREENSHOT: "screenshot",
  DETAIL_URL: "detail_url",
  CATEGORY: "category",
  SHIPPING_CURRENCY: "shipping_cur",
  SHIPPING_COST: "shipping_cost",
  SITE_PRICE: "site_price",
  SITE_PRICE_CUR: "site_price_cur",
  TYPE: "type",
  NOTES: 'notes',
  PID: 'pid',
  IS_LOWEST_PRICE: 'is_lowest_price',
  AUDITS: 'trackers',
}